import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <footer className="bg-ink-900 text-white" data-testid="site-footer">
      <div className="container-wide pt-20 pb-8">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* Brand + newsletter */}
          <div className="lg:col-span-6">
            <p className="max-w-md font-mono text-sm leading-relaxed text-white/70">
              Tailored payment solutions built for coaches, consultants, creators, digital marketers, and publishers. Next-day funding, 24/7 support, no hidden fees.
            </p>
            <form onSubmit={subscribe} className="mt-8 max-w-md" data-testid="footer-newsletter-form">
              <div className="label-brutal !text-white/60">Stay informed</div>
              <div className="mt-2 flex border border-white">
                <input
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent px-4 py-3 font-mono text-sm text-white placeholder:text-white/30 focus:outline-none"
                  data-testid="footer-newsletter-email"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="border-l border-white bg-white px-5 py-3 font-mono text-[11px] font-semibold uppercase tracking-wide2 text-ink-900 transition-colors hover:bg-accent hover:text-white disabled:opacity-60"
                  data-testid="footer-newsletter-submit"
                >
                  {status === "loading" ? "..." : "Subscribe →"}
                </button>
              </div>
              {status === "success" && (
                <p className="mt-3 font-mono text-xs text-accent" data-testid="footer-newsletter-success">
                  ✓ Subscribed. Welcome aboard.
                </p>
              )}
              {status === "error" && (
                <p className="mt-3 font-mono text-xs text-accent">
                  Something went wrong. Try again.
                </p>
              )}
            </form>
          </div>

          {/* Link columns */}
          <FooterCol title="Navigate" testid="footer-col-nav">
            <FooterLink to="/">Index</FooterLink>
            <FooterLink to="/about-us">About</FooterLink>
            <FooterLink to="/#solutions">Solutions</FooterLink>
            <FooterLink to="/#industries">Industries</FooterLink>
            <FooterLink to="/contact-us">Contact</FooterLink>
          </FooterCol>

          <FooterCol title="Solutions" testid="footer-col-solutions">
            <FooterLink to="/#solutions">Merchant accounts</FooterLink>
            <FooterLink to="/#solutions">Alt payment methods</FooterLink>
            <FooterLink to="/#solutions">Chargeback mitigation</FooterLink>
            <FooterLink to="/#solutions">Compliance</FooterLink>
            <FooterLink to="/#solutions">Payouts</FooterLink>
          </FooterCol>

          <FooterCol title="Contact" testid="footer-col-contact">
            <li className="flex items-start gap-3">
              <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
              <a
                href="mailto:info@umg.inc"
                className="font-mono text-xs text-white/70 hover:text-white"
                data-testid="footer-email-link"
              >
                info@umg.inc
              </a>
            </li>
            <li className="font-mono text-xs text-white/50">
              Personalized Support for Every Merchant
            </li>
          </FooterCol>
        </div>

        {/* Massive wordmark */}
        <div className="mt-20 select-none overflow-hidden border-t border-white/15 pt-10">
          <div className="font-display text-[24vw] font-black uppercase leading-[0.85] tracking-tighter text-white/[0.07]">
            UMG
          </div>
        </div>

        <div className="mt-6 border-t border-white/15 pt-6 text-center">
          <p className="font-mono text-[11px] uppercase tracking-wide2 text-white/40" data-testid="footer-copyright">
            COPYRIGHT ©2026 MONEY COMPASS LTD. DBA UMG PAYMENTS. ALL RIGHTS RESERVED.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children, testid }) {
  return (
    <div className="lg:col-span-2" data-testid={testid}>
      <h4 className="font-mono text-[11px] font-semibold uppercase tracking-ultra text-white/50">
        {title}
      </h4>
      <ul className="mt-5 space-y-3">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }) {
  return (
    <li>
      <Link
        to={to}
        className="font-mono text-xs text-white/70 transition-colors hover:text-accent"
      >
        {children}
      </Link>
    </li>
  );
}
